import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Search, Filter, Download, LayoutGrid, List, X, Phone, Mail,
  Tag, GripVertical, ChevronRight, Plus, Trash2, Pin, Send,
  Clock, MessageSquare, Calendar, User, DollarSign, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_TYPES = [
  { id: 'general', name: 'All Leads' },
  { id: 'buyers', name: 'Buyers' },
  { id: 'sellers', name: 'Sellers' },
  { id: 'investors', name: 'Investors' },
  { id: 'property_management', name: 'Property Mgmt' },
];

const TAG_COLORS = {
  buyer: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  seller: 'bg-green-500/20 text-green-400 border-green-500/30',
  investor: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  landlord: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  financing: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  notary: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  agent_recruit: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};

const ALL_TAGS = ['buyer', 'seller', 'investor', 'landlord', 'financing', 'notary', 'agent_recruit'];

// ============ LEAD CARD (Kanban) ============
const LeadCard = ({ lead, onSelect, onStageMove, stages }) => {
  return (
    <div
      className="bg-[#1A1A1A] border border-[#333] rounded-lg p-3 mb-2 cursor-pointer hover:border-[#D4AF37]/40 transition-all group"
      onClick={() => onSelect(lead)}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-[#F5F5F0] text-sm font-medium truncate flex-1">{lead.name}</h4>
        {lead.deal_value > 0 && (
          <span className="text-[#D4AF37] text-xs font-semibold ml-2">${(lead.deal_value / 1000).toFixed(0)}K</span>
        )}
      </div>
      <div className="flex items-center gap-1 mb-2">
        <Mail className="w-3 h-3 text-[#808080]" />
        <span className="text-[#808080] text-xs truncate">{lead.email}</span>
      </div>
      {lead.phone && (
        <div className="flex items-center gap-1 mb-2">
          <Phone className="w-3 h-3 text-[#808080]" />
          <span className="text-[#808080] text-xs">{lead.phone}</span>
        </div>
      )}
      {lead.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {lead.tags.map(tag => (
            <span key={tag} className={`px-1.5 py-0.5 text-[10px] rounded border ${TAG_COLORS[tag] || 'bg-[#333] text-[#808080]'}`}>
              {tag}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#333]">
        <span className="text-[10px] text-[#808080] capitalize">{lead.source?.replace('_', ' ')}</span>
        <span className="text-[10px] text-[#808080]">{new Date(lead.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
};

// ============ KANBAN COLUMN ============
const KanbanColumn = ({ stageId, stageData, onSelectLead, onDrop, stages }) => {
  const stage = stageData.stage;
  const leads = stageData.leads || [];

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-[#D4AF37]/50', 'bg-[#D4AF37]/5');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('border-[#D4AF37]/50', 'bg-[#D4AF37]/5');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-[#D4AF37]/50', 'bg-[#D4AF37]/5');
    const leadId = e.dataTransfer.getData('text/plain');
    if (leadId) onDrop(leadId, stageId);
  };

  return (
    <div
      className="flex-shrink-0 w-[280px] bg-[#111111] border border-[#222] rounded-xl flex flex-col max-h-[calc(100vh-260px)] transition-colors"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`kanban-column-${stageId}`}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-[#222] flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
            <h3 className="text-[#F5F5F0] text-sm font-medium">{stage.name}</h3>
          </div>
          <span className="bg-[#1A1A1A] text-[#D4AF37] text-xs font-semibold px-2 py-0.5 rounded-full">{leads.length}</span>
        </div>
      </div>
      {/* Column Body */}
      <div className="p-2 overflow-y-auto flex-1 custom-scrollbar">
        {leads.length === 0 ? (
          <div className="text-center py-8 text-[#808080] text-xs">No leads</div>
        ) : (
          leads.map(lead => (
            <div
              key={lead.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', lead.id);
                e.currentTarget.style.opacity = '0.5';
              }}
              onDragEnd={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <LeadCard lead={lead} onSelect={onSelectLead} stages={[]} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============ LEAD DETAIL PANEL ============
const CALL_OUTCOMES_MINI = [
  { id: 'answered', label: 'Answered', color: '#22C55E' },
  { id: 'voicemail', label: 'Voicemail', color: '#F59E0B' },
  { id: 'no_answer', label: 'No Answer', color: '#EF4444' },
  { id: 'busy', label: 'Busy', color: '#808080' },
];

const LeadDetailPanel = ({ leadId, onClose, onUpdate, stages }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [noteText, setNoteText] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [agents, setAgents] = useState([]);
  const [showCallLog, setShowCallLog] = useState(false);
  const [callForm, setCallForm] = useState({ outcome: 'answered', mins: 0, secs: 0, notes: '' });
  const [callLoading, setCallLoading] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads/${leadId}`);
      setData(res.data);
      setEditForm({
        name: res.data.lead.name,
        email: res.data.lead.email,
        phone: res.data.lead.phone || '',
        stage: res.data.lead.stage,
        pipeline: res.data.lead.pipeline,
        tags: res.data.lead.tags || [],
        deal_value: res.data.lead.deal_value || 0,
        budget: res.data.lead.budget || '',
        timeline: res.data.lead.timeline || '',
        property_interest: res.data.lead.property_interest || '',
      });
    } catch {
      toast.error('Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  useEffect(() => {
    axios.get(`${API}/agents`).then(res => setAgents(res.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/leads/${leadId}`, editForm);
      toast.success('Lead updated');
      setEditMode(false);
      fetchLead();
      onUpdate();
    } catch {
      toast.error('Failed to update lead');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/leads/${leadId}/notes?content=${encodeURIComponent(noteText)}`);
      setNoteText('');
      fetchLead();
      toast.success('Note added');
    } catch {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}/notes/${noteId}`);
      fetchLead();
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const handleToggleTag = async (tag) => {
    const currentTags = editForm.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    try {
      await axios.put(`${API}/leads/${leadId}`, { tags: newTags });
      setEditForm({...editForm, tags: newTags});
      fetchLead();
      onUpdate();
      toast.success(`Tag ${currentTags.includes(tag) ? 'removed' : 'added'}`);
    } catch {
      toast.error('Failed to update tags');
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await axios.put(`${API}/leads/${leadId}/stage/${newStage}`);
      setEditForm({...editForm, stage: newStage});
      fetchLead();
      onUpdate();
      toast.success('Stage updated');
    } catch {
      toast.error('Failed to update stage');
    }
  };

  const handleAssignAgent = async (agentId) => {
    try {
      if (agentId) {
        await axios.put(`${API}/leads/${leadId}/assign/${agentId}`);
        toast.success('Lead assigned');
      } else {
        await axios.put(`${API}/leads/${leadId}/unassign`);
        toast.success('Lead unassigned');
      }
      fetchLead();
      onUpdate();
    } catch {
      toast.error('Failed to assign');
    }
  };

  const handleLogCall = async () => {
    setCallLoading(true);
    const totalSeconds = (parseInt(callForm.mins) || 0) * 60 + (parseInt(callForm.secs) || 0);
    const outcomeLabel = CALL_OUTCOMES_MINI.find(o => o.id === callForm.outcome)?.label || callForm.outcome;
    try {
      await axios.post(`${API}/leads/${leadId}/activities`, {
        lead_id: leadId,
        activity_type: 'call',
        title: `Call - ${outcomeLabel}`,
        description: callForm.notes,
        call_duration: totalSeconds,
        call_outcome: callForm.outcome,
      });
      toast.success('Call logged');
      setShowCallLog(false);
      setCallForm({ outcome: 'answered', mins: 0, secs: 0, notes: '' });
      fetchLead();
    } catch {
      toast.error('Failed to log call');
    } finally {
      setCallLoading(false);
    }
  };

  if (loading) return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0a0a0a] h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
        <div className="spinner" />
      </div>
    </div>
  );

  if (!data) return null;
  const { lead, activities = [], notes = [] } = data;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose} data-testid="lead-detail-panel">
      <div className="w-full max-w-lg bg-[#0a0a0a] h-full overflow-y-auto border-l border-[#D4AF37]/20" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-[#222] p-4 flex items-center justify-between">
          <h2 className="text-xl text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>Lead Details</h2>
          <button onClick={onClose} className="text-[#808080] hover:text-[#F5F5F0]" data-testid="close-lead-detail">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Lead Info */}
          <div className="bg-[#111111] border border-[#222] rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg text-[#F5F5F0] font-medium">{lead.name}</h3>
              <button onClick={() => setEditMode(!editMode)} className="text-[#D4AF37] text-sm hover:underline" data-testid="toggle-edit-lead">
                {editMode ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {editMode ? (
              <div className="space-y-3">
                <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} placeholder="Name" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="edit-lead-name" />
                <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} placeholder="Email" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="edit-lead-email" />
                <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} placeholder="Phone" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="edit-lead-phone" />
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" value={editForm.deal_value} onChange={e => setEditForm({...editForm, deal_value: parseFloat(e.target.value) || 0})} placeholder="Deal Value" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="edit-deal-value" />
                  <Input value={editForm.budget} onChange={e => setEditForm({...editForm, budget: e.target.value})} placeholder="Budget" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="edit-budget" />
                </div>
                <Input value={editForm.property_interest} onChange={e => setEditForm({...editForm, property_interest: e.target.value})} placeholder="Property Interest" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="edit-property-interest" />
                <Input value={editForm.timeline} onChange={e => setEditForm({...editForm, timeline: e.target.value})} placeholder="Timeline" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="edit-timeline" />
                <Button onClick={handleSave} className="btn-gold w-full" data-testid="save-lead-changes">Save Changes</Button>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                  <a href={`mailto:${lead.email}`} className="text-[#D4AF37] hover:underline">{lead.email}</a>
                </div>
                {lead.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#808080]" />
                    <a href={`tel:${lead.phone}`} className="text-[#808080] hover:text-[#D4AF37]">{lead.phone}</a>
                  </div>
                )}
                {lead.property_interest && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#808080]" />
                    <span className="text-[#808080]">{lead.property_interest}</span>
                  </div>
                )}
                {lead.deal_value > 0 && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-[#D4AF37] font-semibold">${lead.deal_value.toLocaleString()}</span>
                  </div>
                )}
                {lead.message && (
                  <p className="text-[#808080] mt-2 p-2 bg-[#1A1A1A] rounded text-xs">{lead.message}</p>
                )}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <a href={lead.phone ? `tel:${lead.phone}` : '#'} className="flex-1">
              <Button className="btn-gold w-full text-xs" disabled={!lead.phone} data-testid="quick-call-lead">
                <Phone className="w-3.5 h-3.5 mr-1" /> Call
              </Button>
            </a>
            <Button onClick={() => setShowCallLog(!showCallLog)} variant="outline" className="btn-outline-gold flex-1 text-xs" data-testid="quick-log-call">
              <Clock className="w-3.5 h-3.5 mr-1" /> Log Call
            </Button>
            <a href={lead.email ? `mailto:${lead.email}` : '#'} className="flex-1">
              <Button variant="outline" className="btn-outline-gold w-full text-xs" data-testid="quick-email-lead">
                <Mail className="w-3.5 h-3.5 mr-1" /> Email
              </Button>
            </a>
          </div>

          {/* Call Logger (inline) */}
          {showCallLog && (
            <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-lg p-4" data-testid="inline-call-logger">
              <h4 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3 font-medium">Log Call</h4>
              <div className="grid grid-cols-4 gap-1.5 mb-3">
                {CALL_OUTCOMES_MINI.map(o => (
                  <button
                    key={o.id}
                    onClick={() => setCallForm({...callForm, outcome: o.id})}
                    className={`px-2 py-1.5 rounded text-[10px] font-medium transition-all ${
                      callForm.outcome === o.id
                        ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30'
                        : 'bg-[#1A1A1A] text-[#808080] border border-[#333]'
                    }`}
                    data-testid={`inline-outcome-${o.id}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mb-3">
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="120" value={callForm.mins}
                    onChange={e => setCallForm({...callForm, mins: e.target.value})}
                    className="w-12 px-2 py-1.5 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-xs text-center"
                    data-testid="inline-call-mins" />
                  <span className="text-[#808080] text-[10px]">m</span>
                </div>
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="59" value={callForm.secs}
                    onChange={e => setCallForm({...callForm, secs: e.target.value})}
                    className="w-12 px-2 py-1.5 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-xs text-center"
                    data-testid="inline-call-secs" />
                  <span className="text-[#808080] text-[10px]">s</span>
                </div>
              </div>
              <textarea
                value={callForm.notes}
                onChange={e => setCallForm({...callForm, notes: e.target.value})}
                placeholder="Call notes..."
                rows={2}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-xs mb-3 resize-none"
                data-testid="inline-call-notes"
              />
              <div className="flex gap-2">
                <Button onClick={() => setShowCallLog(false)} variant="outline" className="btn-outline-gold flex-1 text-xs" size="sm">Cancel</Button>
                <Button onClick={handleLogCall} className="btn-gold flex-1 text-xs" size="sm" disabled={callLoading} data-testid="inline-submit-call">
                  {callLoading ? 'Logging...' : 'Log Call'}
                </Button>
              </div>
            </div>
          )}

          {/* Pipeline Stage */}
          <div className="bg-[#111111] border border-[#222] rounded-lg p-4">
            <h4 className="text-[#808080] text-xs uppercase tracking-wider mb-3 font-medium">Pipeline Stage</h4>
            <div className="flex flex-wrap gap-1.5">
              {stages.map(s => (
                <button
                  key={s.id}
                  onClick={() => handleStageChange(s.id)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                    lead.stage === s.id
                      ? 'ring-2 ring-offset-1 ring-offset-[#111111]'
                      : 'opacity-50 hover:opacity-80'
                  }`}
                  style={{
                    backgroundColor: `${s.color}20`,
                    color: s.color,
                    ...(lead.stage === s.id ? { ringColor: s.color } : {})
                  }}
                  data-testid={`stage-btn-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="bg-[#111111] border border-[#222] rounded-lg p-4">
            <h4 className="text-[#808080] text-xs uppercase tracking-wider mb-3 font-medium flex items-center gap-2">
              <Tag className="w-3.5 h-3.5" /> Tags
            </h4>
            <div className="flex flex-wrap gap-2">
              {ALL_TAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleToggleTag(tag)}
                  className={`px-2.5 py-1 rounded text-xs border transition-all ${
                    (editForm.tags || []).includes(tag)
                      ? TAG_COLORS[tag]
                      : 'bg-[#1A1A1A] text-[#808080] border-[#333] opacity-40 hover:opacity-70'
                  }`}
                  data-testid={`tag-toggle-${tag}`}
                >
                  {tag.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Agent Assignment */}
          {agents.length > 0 && (
            <div className="bg-[#111111] border border-[#222] rounded-lg p-4">
              <h4 className="text-[#808080] text-xs uppercase tracking-wider mb-3 font-medium flex items-center gap-2">
                <User className="w-3.5 h-3.5" /> Assigned Agent
              </h4>
              <select
                value={lead.assigned_to || ''}
                onChange={e => handleAssignAgent(e.target.value || null)}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded-lg text-[#F5F5F0] text-sm"
                data-testid="assign-agent-select"
              >
                <option value="">Unassigned</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div className="bg-[#111111] border border-[#222] rounded-lg p-4">
            <h4 className="text-[#808080] text-xs uppercase tracking-wider mb-3 font-medium">Notes</h4>
            <div className="flex gap-2 mb-3">
              <Textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note..."
                rows={2}
                className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] text-sm flex-1"
                data-testid="note-input"
              />
              <Button onClick={handleAddNote} className="btn-gold self-end" size="sm" data-testid="add-note-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {notes.map(note => (
                <div key={note.id} className="p-2 bg-[#1A1A1A] rounded text-xs text-[#F5F5F0] group flex justify-between items-start">
                  <div>
                    <p>{note.content}</p>
                    <span className="text-[#808080] text-[10px]">{new Date(note.created_at).toLocaleString()}</span>
                  </div>
                  <button onClick={() => handleDeleteNote(note.id)} className="opacity-0 group-hover:opacity-100 text-red-400 ml-2" data-testid={`delete-note-${note.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {notes.length === 0 && <p className="text-[#808080] text-xs text-center py-2">No notes yet</p>}
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="bg-[#111111] border border-[#222] rounded-lg p-4">
            <h4 className="text-[#808080] text-xs uppercase tracking-wider mb-3 font-medium">Activity Timeline</h4>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {activities.map(act => (
                <div key={act.id} className="flex gap-3 text-xs">
                  <div className="w-6 h-6 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    {act.activity_type === 'call' ? <Phone className="w-3 h-3 text-blue-400" /> :
                     act.activity_type === 'stage_change' ? <ArrowRight className="w-3 h-3 text-[#D4AF37]" /> :
                     act.activity_type === 'note' ? <MessageSquare className="w-3 h-3 text-blue-400" /> :
                     act.activity_type === 'tag_change' ? <Tag className="w-3 h-3 text-purple-400" /> :
                     act.activity_type === 'email' ? <Mail className="w-3 h-3 text-purple-400" /> :
                     act.activity_type === 'sms' ? <Send className="w-3 h-3 text-green-400" /> :
                     <Clock className="w-3 h-3 text-[#808080]" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-[#F5F5F0]">{act.title}</p>
                    {act.description && <p className="text-[#808080]">{act.description}</p>}
                    {act.activity_type === 'call' && (
                      <div className="flex items-center gap-2 mt-0.5">
                        {act.call_outcome && (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                            act.call_outcome === 'answered' ? 'bg-green-500/20 text-green-400' :
                            act.call_outcome === 'voicemail' ? 'bg-yellow-500/20 text-yellow-400' :
                            act.call_outcome === 'no_answer' ? 'bg-red-500/20 text-red-400' :
                            'bg-[#333] text-[#808080]'
                          }`}>{act.call_outcome?.replace('_', ' ')}</span>
                        )}
                        {act.call_duration > 0 && (
                          <span className="text-[#808080] text-[9px]">{Math.floor(act.call_duration / 60)}m {act.call_duration % 60}s</span>
                        )}
                      </div>
                    )}
                    <span className="text-[#808080] text-[10px]">{new Date(act.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
              {activities.length === 0 && <p className="text-[#808080] text-xs text-center py-2">No activity yet</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN ADMIN LEADS PAGE ============
const getUserRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
    return user.role || 'admin';
  } catch { return 'admin'; }
};

export const AdminLeads = () => {
  const [viewMode, setViewMode] = useState('kanban');
  const [pipeline, setPipeline] = useState('general');
  const [kanbanData, setKanbanData] = useState({});
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [stages, setStages] = useState([]);
  const role = getUserRole();
  const isAgent = role === 'agent';

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    fetchData();
  }, [pipeline, viewMode]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/config/pipelines`);
      setStages(res.data.stages);
    } catch (error) { console.error('Config fetch error:', error); }
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      if (viewMode === 'kanban') {
        const url = isAgent
          ? `${API}/agent/pipeline/${pipeline}?token=${token}`
          : `${API}/leads/pipeline/${pipeline}`;
        const res = await axios.get(url);
        setKanbanData(res.data);
      } else {
        const url = isAgent
          ? `${API}/agent/leads?token=${token}`
          : `${API}/leads`;
        const res = await axios.get(url);
        const data = Array.isArray(res.data) ? res.data : res.data.leads || [];
        setLeads(data);
      }
    } catch {
      toast.error('Error fetching leads');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/leads/${leadId}/stage/${newStage}`);
      toast.success('Lead moved');
      fetchData();
    } catch {
      toast.error('Failed to move lead');
    }
  };

  const handleExportCSV = () => {
    let url = `${API}/leads/export/csv`;
    const params = new URLSearchParams();
    if (pipeline !== 'general') params.append('pipeline', pipeline);
    if (filterTag) params.append('tag', filterTag);
    if (params.toString()) url += `?${params.toString()}`;
    window.open(url, '_blank');
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !search || 
      lead.name?.toLowerCase().includes(search.toLowerCase()) ||
      lead.email?.toLowerCase().includes(search.toLowerCase());
    const matchesTag = !filterTag || (lead.tags || []).includes(filterTag);
    return matchesSearch && matchesTag;
  });

  return (
    <AdminLayout>
      <div data-testid="admin-leads-crm">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              {isAgent ? 'My Leads' : 'CRM Pipeline'}
            </h1>
            <p className="text-[#808080] mt-1">{isAgent ? 'Leads assigned to you' : 'Manage your leads through the sales pipeline'}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex bg-[#111111] border border-[#333] rounded-lg overflow-hidden" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${viewMode === 'kanban' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-[#808080] hover:text-[#F5F5F0]'}`}
                data-testid="view-kanban-btn"
              >
                <LayoutGrid className="w-4 h-4" /> Board
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${viewMode === 'table' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-[#808080] hover:text-[#F5F5F0]'}`}
                data-testid="view-table-btn"
              >
                <List className="w-4 h-4" /> Table
              </button>
            </div>
            <Button onClick={handleExportCSV} variant="outline" className="btn-outline-gold text-sm" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        </div>

        {/* Pipeline Tabs + Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex bg-[#111111] border border-[#222] rounded-lg overflow-hidden" data-testid="pipeline-filter">
            {PIPELINE_TYPES.map(p => (
              <button
                key={p.id}
                onClick={() => setPipeline(p.id)}
                className={`px-3 py-2 text-xs font-medium transition-colors ${pipeline === p.id ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-[#808080] hover:text-[#F5F5F0]'}`}
                data-testid={`pipeline-${p.id}`}
              >
                {p.name}
              </button>
            ))}
          </div>
          {viewMode === 'table' && (
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
                <Input
                  type="text"
                  placeholder="Search leads..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-10 bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="table-search"
                />
              </div>
              <select
                value={filterTag}
                onChange={e => setFilterTag(e.target.value)}
                className="px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0] text-sm"
                data-testid="table-tag-filter"
              >
                <option value="">All Tags</option>
                {ALL_TAGS.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner" /></div>
        ) : viewMode === 'kanban' ? (
          /* Kanban Board */
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
            {stages.map(stage => (
              <KanbanColumn
                key={stage.id}
                stageId={stage.id}
                stageData={kanbanData[stage.id] || { stage, leads: [] }}
                onSelectLead={(lead) => setSelectedLeadId(lead.id)}
                onDrop={handleDrop}
                stages={stages}
              />
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="dashboard-card overflow-x-auto" data-testid="leads-table">
            {filteredLeads.length === 0 ? (
              <p className="text-[#808080] text-center py-16">No leads found</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#D4AF37]/10">
                    <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Contact</th>
                    <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Tags</th>
                    <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Stage</th>
                    <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Pipeline</th>
                    <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => {
                    const stageInfo = stages.find(s => s.id === lead.stage);
                    return (
                      <tr key={lead.id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]/50 cursor-pointer" onClick={() => setSelectedLeadId(lead.id)} data-testid={`table-row-${lead.id}`}>
                        <td className="py-3 px-4">
                          <p className="text-[#F5F5F0] font-medium text-sm">{lead.name}</p>
                          <p className="text-[#808080] text-xs capitalize">{lead.source?.replace('_', ' ')}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-[#D4AF37] text-xs">{lead.email}</p>
                          {lead.phone && <p className="text-[#808080] text-xs">{lead.phone}</p>}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {(lead.tags || []).map(tag => (
                              <span key={tag} className={`px-1.5 py-0.5 text-[10px] rounded border ${TAG_COLORS[tag] || 'bg-[#333] text-[#808080]'}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {stageInfo && (
                            <span className="px-2 py-1 text-xs rounded" style={{ backgroundColor: `${stageInfo.color}20`, color: stageInfo.color }}>
                              {stageInfo.name}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-[#808080] text-xs capitalize">{lead.pipeline?.replace('_', ' ')}</td>
                        <td className="py-3 px-4 text-[#808080] text-xs">{new Date(lead.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4">
                          <Button size="sm" variant="outline" className="btn-outline-gold text-xs" onClick={e => { e.stopPropagation(); setSelectedLeadId(lead.id); }} data-testid={`view-lead-${lead.id}`}>
                            <ChevronRight className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Lead Detail Panel */}
        {selectedLeadId && (
          <LeadDetailPanel
            leadId={selectedLeadId}
            onClose={() => setSelectedLeadId(null)}
            onUpdate={fetchData}
            stages={stages}
          />
        )}
      </div>
    </AdminLayout>
  );
};
